import { FormattedMessage, defineMessage } from 'react-intl';
import styled from 'styled-components';

import { FootballRivalsCompetitionSortType } from '@sorare/core/src/__generated__/globalTypes';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import Dropdown from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Sort } from '@sorare/core/src/atoms/icons/Sort';
import Radio from '@sorare/core/src/atoms/inputs/Radio';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelM, LabelS } from '@sorare/core/src/atoms/typography';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

const Root = styled(Vertical)`
  padding: var(--double-unit);

  border-radius: var(--double-unit) var(--double-unit) 0 0;
  background-color: var(--c-neutral-200);
  @media ${tabletAndAbove} {
    padding-top: 0;
  }
`;
const RadioButtons = styled(Vertical)`
  padding: var(--double-unit) 0;

  & > :not(:last-child) {
    padding-bottom: var(--double-unit);
    border-bottom: 1px solid var(--c-neutral-300);
  }
`;
const MobileOnly = styled.div`
  @media ${tabletAndAbove} {
    display: none;
  }
`;

export const sortings = {
  [FootballRivalsCompetitionSortType.FAVORITE_COMPETITION]: {
    value: FootballRivalsCompetitionSortType.FAVORITE_COMPETITION,
    label: defineMessage({
      id: 'SortBy.sortings.favoriteCompetition.label',
      defaultMessage: 'Alphabetical (A-Z)',
    }),
    shortLabel: defineMessage({
      id: 'SortBy.sortings.favoriteCompetition.shortLabel',
      defaultMessage: 'A-Z',
    }),
  },
  [FootballRivalsCompetitionSortType.GAME_DATE]: {
    value: FootballRivalsCompetitionSortType.GAME_DATE,
    label: defineMessage({
      id: 'SortBy.sortings.gameDate.label',
      defaultMessage: 'Time (Starting Soon)',
    }),
    shortLabel: defineMessage({
      id: 'SortBy.sortings.gameDate.shortLabel',
      defaultMessage: 'Starting Soon',
    }),
  },
};

type Props = {
  sortBy: FootballRivalsCompetitionSortType;
  onSortChange: (sorting: FootballRivalsCompetitionSortType) => void;
};
export const SortBy = ({ sortBy, onSortChange }: Props) => {
  return (
    <Dropdown
      label={props => (
        <Horizontal as={ButtonBase} {...props}>
          <LabelS as="h3" bold>
            <FormattedMessage id="SortByCta.title" defaultMessage="Sort By" />
          </LabelS>
          <Sort />
        </Horizontal>
      )}
      closeOnChange
      gap={16}
    >
      <Root>
        <MobileOnly>
          <HeadlineM as="h3">
            <FormattedMessage id="SortBy.title" defaultMessage="Sort by:" />
          </HeadlineM>
        </MobileOnly>
        <RadioButtons gap={2}>
          {Object.values(sortings).map(({ value, label }) => (
            <Radio
              key={value}
              name={value}
              value={value}
              checked={value === sortBy}
              onChange={({ target }) =>
                onSortChange(target.value as FootballRivalsCompetitionSortType)
              }
              labelContent={
                <LabelM as="span">
                  <FormattedMessage {...label} />
                </LabelM>
              }
            />
          ))}
        </RadioButtons>
      </Root>
    </Dropdown>
  );
};
